import { z } from 'zod'

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20
export const PASSWORD_MIN_LENGTH = 6
// bcrypt has a limit of 72 bytes (which should be plenty long)
// https://github.com/epicweb-dev/epic-stack/issues/918
export const PASSWORD_MAX_BYTES = 72

type ValidationTranslator = (key: string, defaultMessage: string) => string

const fallbackValidationTranslator: ValidationTranslator = (
	_key,
	defaultMessage,
) => defaultMessage

export function createUsernameSchema(
	translate: ValidationTranslator = fallbackValidationTranslator,
) {
	return (
		z
			.string({
				required_error: translate(
					'auth.errors.usernameRequired',
					'Username is required',
				),
			})
			.min(USERNAME_MIN_LENGTH, {
				message: translate(
					'auth.errors.usernameTooShort',
					`Username must be at least ${USERNAME_MIN_LENGTH} characters`,
				),
			})
			.max(USERNAME_MAX_LENGTH, {
				message: translate(
					'auth.errors.usernameTooLong',
					`Username cannot exceed ${USERNAME_MAX_LENGTH} characters`,
				),
			})
			.regex(/^[a-zA-Z0-9_]+$/, {
				message: translate(
					'auth.errors.usernameInvalidFormat',
					'Username can only include letters, numbers, and underscores',
				),
			})
			// users can type the username in any case, but we store it in lowercase
			.transform((value) => value.toLowerCase())
	)
}

export function createPasswordSchema(
	translate: ValidationTranslator = fallbackValidationTranslator,
) {
	return z
		.string({
			required_error: translate(
				'auth.errors.passwordRequired',
				'Password is required',
			),
		})
		.min(PASSWORD_MIN_LENGTH, {
			message: translate(
				'auth.errors.passwordTooShort',
				'Password is too short',
			),
		})
		.refine(
			(val) => new TextEncoder().encode(val).length <= PASSWORD_MAX_BYTES,
			{
				message: translate(
					'auth.errors.passwordTooLong',
					'Password is too long',
				),
			},
		)
}

export function createNameSchema(
	translate: ValidationTranslator = fallbackValidationTranslator,
) {
	return z
		.string({
			required_error: translate('auth.errors.nameRequired', 'Name is required'),
		})
		.min(3, {
			message: translate('auth.errors.nameTooShort', 'Name is too short'),
		})
		.max(40, {
			message: translate('auth.errors.nameTooLong', 'Name is too long'),
		})
}

export function createEmailSchema(
	translate: ValidationTranslator = fallbackValidationTranslator,
) {
	return (
		z
			.string({
				required_error: translate(
					'auth.errors.emailRequired',
					'Email is required',
				),
			})
			.email({
				message: translate('auth.errors.emailInvalid', 'Email is invalid'),
			})
			.min(3, {
				message: translate('auth.errors.emailTooShort', 'Email is too short'),
			})
			.max(100, {
				message: translate('auth.errors.emailTooLong', 'Email is too long'),
			})
			// users can type the email in any case, but we store it in lowercase
			.transform((value) => value.toLowerCase())
	)
}

export function createUsernameOrEmailSchema(
	translate: ValidationTranslator = fallbackValidationTranslator,
) {
	return z.union([
		createEmailSchema(translate),
		createUsernameSchema(translate),
	])
}

export function createPasswordAndConfirmPasswordSchema(
	translate: ValidationTranslator = fallbackValidationTranslator,
) {
	const passwordSchema = createPasswordSchema(translate)
	return z
		.object({ password: passwordSchema, confirmPassword: passwordSchema })
		.superRefine(({ confirmPassword, password }, ctx) => {
			if (confirmPassword !== password) {
				ctx.addIssue({
					path: ['confirmPassword'],
					code: 'custom',
					message: translate(
						'auth.errors.passwordsMustMatch',
						'The passwords must match',
					),
				})
			}
		})
}

export const UsernameSchema = createUsernameSchema()
export const PasswordSchema = createPasswordSchema()
export const NameSchema = createNameSchema()
export const EmailSchema = createEmailSchema()
export const UsernameOrEmailSchema = createUsernameOrEmailSchema()
export const PasswordAndConfirmPasswordSchema =
	createPasswordAndConfirmPasswordSchema()
